import { MouseEvent, memo } from 'react';

import { faChevronDown, faSquare } from '@fortawesome/pro-solid-svg-icons';
import { useLinkClickHandler, useMatches } from 'react-router-dom';
import { styled } from 'styled-components';

import { useEvent } from 'core/hooks';
import { Icon } from 'core/ui';

import { NavbarLinkItem } from '../types/NavbarLinkItem';

type NavbarLinkProps = {
  item: NavbarLinkItem;
  mode: 'dialog' | 'mini' | 'full';
  nested?: boolean;
  expandedItemIds: string[];
  onExpandClick?: (routeId: number) => void;
  onLinkClick?: () => void;
};

export const NavbarLink = memo<NavbarLinkProps>(({ item, mode, nested, expandedItemIds, onExpandClick, onLinkClick }) => {
  const matches = useMatches();

  const linkClickHandlerInternal = useLinkClickHandler('to' in item ? item.to : '');

  const linkClickHandler = useEvent((event: MouseEvent<HTMLAnchorElement>) => {
    linkClickHandlerInternal(event);

    if (mode === 'dialog') {
      onLinkClick?.();
    }
  });

  const handleExpandClick = useEvent((event: MouseEvent<HTMLButtonElement>) => {
    const routeId = Number.parseInt(event.currentTarget.dataset.routeId!, 10);
    onExpandClick?.(routeId);
  });

  if ('to' in item) {
    return (
      <StyledLinkAnchor
        data-route-id={item.id}
        href={item.to}
        onClick={linkClickHandler}
        className={matches.some((m) => m.id === item.id.toString()) ? 'selected' : undefined}
        title={item.title}
        $nested={nested}
        $mode={mode}
      >
        <StyledIcon icon={item.icon ?? faSquare} fixedWidth />
        <StyledTextDiv $mode={mode}>
          <StyledTextInnerDiv $mode={mode}>{item.text}</StyledTextInnerDiv>
        </StyledTextDiv>
      </StyledLinkAnchor>
    );
  } else if ('href' in item) {
    return (
      <StyledLinkAnchor href={item.href} title={item.title} $nested={nested} $mode={mode}>
        <StyledIcon icon={item.icon ?? faSquare} fixedWidth />
        <StyledTextDiv $mode={mode}>
          <StyledTextInnerDiv $mode={mode}>{item.text}</StyledTextInnerDiv>
        </StyledTextDiv>
      </StyledLinkAnchor>
    );
  } else {
    // We only want to show an expanded navbar section when NOT in mini mode.  Because an expanded navbar section runs the risk of requiring
    // a scrollbar.  This currently will break the layout since the width of the navbar is just barely big enough to show icons.  Making it
    // dynamic enough to expand to the correct size with scrollbars is difficult (though not impossible).
    const showExpanded = expandedItemIds.includes(item.id.toString()) && mode !== 'mini';

    return (
      <>
        <StyledLinkButton key={item.id} data-route-id={item.id} type="button" onClick={handleExpandClick} $mode={mode} title={item.title}>
          <StyledIcon icon={item.icon} fixedWidth />
          <StyledTextDiv $mode={mode}>
            <StyledTextInnerDiv $mode={mode}>{item.text}</StyledTextInnerDiv>
          </StyledTextDiv>
          <StyledExpandIcon icon={faChevronDown} fixedWidth $expanded={showExpanded} />
        </StyledLinkButton>

        <StyledNavbarSectionDiv $expanded={showExpanded}>
          {item.children.map((child) => (
            <NavbarLink key={child.id} item={child} mode={mode} nested expandedItemIds={expandedItemIds} onExpandClick={onExpandClick} />
          ))}
        </StyledNavbarSectionDiv>
      </>
    );
  }
});

NavbarLink.displayName = 'NavbarLink';

const StyledLinkAnchor = styled.a<{ $nested?: boolean; $mode: 'dialog' | 'mini' | 'full' }>`
  display: grid;
  grid-template-columns: min-content minmax(0, 1fr) 4px;
  grid-template-rows: calc(1lh + 15px) min-content;
  color: ${({ theme }) => theme.colors.palette.white};
  text-decoration: none;
  user-select: none;
  padding-left: ${({ $nested, $mode }) => ($nested && $mode !== 'mini' ? '15px' : '10px')};

  &:hover {
    color: ${({ theme }) => theme.colors.palette.white};
    background-color: ${({ theme }) => theme.colors.palette.blues[4]};
  }

  &::after {
    content: '';
    display: block;
    border-left: 3px solid transparent;
  }

  &.selected::after {
    border-left-color: ${({ theme }) => theme.colors.palette.white};
  }
`;

const StyledLinkButton = styled.button<{ $mode: 'dialog' | 'mini' | 'full' }>`
  // Remove default styles.
  user-select: none;
  border: none;
  background-color: transparent;
  text-align: left;

  display: grid;
  grid-template-columns: min-content minmax(20px, 1fr) min-content;
  grid-template-rows: calc(1lh + 15px) min-content;
  color: ${({ theme }) => theme.colors.palette.white};
  text-decoration: none;
  user-select: none;
  padding: 0 5px 0 10px;

  &:hover {
    color: ${({ theme }) => theme.colors.palette.white};
    background-color: ${({ theme }) => theme.colors.palette.blues[4]};
  }
`;

const StyledIcon = styled(Icon)`
  align-self: center;
`;

const StyledTextDiv = styled.div<{ $mode: 'dialog' | 'mini' | 'full' }>`
  grid-column: 2 / span 1;
  grid-row: 1 / span 2;
  align-self: center;
  padding: 4px 10px 4px 17px;
  text-wrap: balance;
  overflow: hidden;
`;

const StyledTextInnerDiv = styled.div<{ $mode: 'dialog' | 'mini' | 'full' }>`
  height: ${({ $mode }) => ($mode === 'mini' ? '1lh' : 'auto')};
  overflow: hidden;
`;

const StyledExpandIcon = styled(Icon)<{ $expanded: boolean }>`
  align-self: center;
  background-color: inherit;

  rotate: ${({ $expanded }) => ($expanded ? '-180deg' : '0deg')};
`;

const StyledNavbarSectionDiv = styled.div<{ $expanded: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: end;
  overflow: hidden;
  user-select: none;
  height: ${({ $expanded }) => ($expanded ? 'auto' : '0')};
`;
